import moment from 'moment';
import React, { useMemo, useState } from 'react';
import Timeline from 'react-calendar-timeline';
// Correct CSS import for v0.30+ (exports field)
import 'react-calendar-timeline/style.css';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Paper, Tooltip } from '@mui/material';

// Helper to compute status
const getStatus = (start, end) => {
  const now = Date.now() / 1000;
  if (start > now) return 'In attesa';
  if (end > now) return 'In corso';
  return 'Scaduta';
};

const statusColors = {
  'In attesa': '#ff9800',
  'In corso': '#4caf50',
  'Scaduta': '#f44336',
};

/*
  Props:
  - bookings: array from API (contains restareas, start, end, users, vehicles)
*/
const PitchGantt = ({ bookings = [] }) => {
  const [rangeMode, setRangeMode] = useState('week');

  const { groups, items } = useMemo(() => {
    const groupMap = new Map();
    const items = bookings.map(b => {
      const rest = b.restareas || {}; // fallback
      const groupId = rest.id || rest.restareaId || 'unknown';
      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, {
          id: groupId,
            title: rest.friendlyName || `Piazzola ${groupId}`,
        });
      }
      const status = getStatus(b.start, b.end);
      const startMoment = moment(b.start * 1000);
      const endMoment = moment(b.end * 1000);
      // Restore full date + time label
      const dateLabel = `${startMoment.format('DD/MM HH:mm')} → ${endMoment.format('DD/MM HH:mm')}`;
      const userLabel = `${b.users?.lastName || ''} ${b.users?.firstName || ''}`.trim();
      return {
        id: b.id,
        group: groupId,
        // Title now excludes license plate, includes date range
        title: userLabel ? `${userLabel} | ${dateLabel}` : dateLabel,
        start_time: moment(b.start * 1000),
        end_time: moment(b.end * 1000),
        itemProps: {
          style: {
            background: statusColors[status] || '#90a4ae',
            borderColor: statusColors[status] || '#90a4ae',
            color: '#fff',
            fontSize: 12,
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          },
          'data-status': status,
        },
      };
    });
    // Sort groups ascending by numeric id (if numeric) or by title
    const groupArr = Array.from(groupMap.values()).sort((a, b) => {
      const aNum = Number(a.id);
      const bNum = Number(b.id);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return (a.title || '').localeCompare(b.title || '');
    });
    return { groups: groupArr, items };
  }, [bookings]);

  const defaultTimeStart = useMemo(() => {
    const base = moment();
    switch (rangeMode) {
      case 'today':
        return base.clone().startOf('day').subtract(6, 'hours');
      case 'month':
        return base.clone().startOf('day').subtract(3, 'days');
      case 'week':
      default:
        return base.clone().startOf('day').subtract(1, 'days');
    }
  }, [rangeMode]);

  const defaultTimeEnd = useMemo(() => {
    const base = moment();
    switch (rangeMode) {
      case 'today':
        return base.clone().endOf('day').add(6, 'hours');
      case 'month':
        return base.clone().add(30, 'days');
      case 'week':
      default:
        return base.clone().add(7, 'days');
    }
  }, [rangeMode]);

  // Dynamic height = rows * lineHeight + header allowance
  const lineHeight = 42; // must match Timeline prop
  const headerAllowance = 90; // approx space for headers/padding
  const dynamicHeight = groups.length * lineHeight + headerAllowance;

  return (
    <Paper sx={{ p: 2, mt: 2, overflow: 'hidden' }}>
      {/* Local style overrides to keep timeline within container */}
      <style>{`
        .react-calendar-timeline .rct-outer { width: 100% !important; }
        .react-calendar-timeline .rct-scroll { width: 100% !important; }
        .react-calendar-timeline .rct-sidebar { min-width: 160px; }
        .react-calendar-timeline .rct-item { cursor: default; }
      `}</style>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Occupazione Piazzole (Gantt)</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={rangeMode}
          onChange={(e, v) => v && setRangeMode(v)}
        >
          <ToggleButton value="today">Oggi</ToggleButton>
          <ToggleButton value="week">Settimana</ToggleButton>
          <ToggleButton value="month">Mese</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ height: dynamicHeight, width: '100%', overflowX: 'auto', overflowY: 'hidden', pr: 1 }}>
        <Timeline
          groups={groups}
          items={items}
          defaultTimeStart={defaultTimeStart}
          defaultTimeEnd={defaultTimeEnd}
          lineHeight={lineHeight}
          itemHeightRatio={0.8}
          canMove={false}
          canResize={false}
          canChangeGroup={false}
          stackItems
          sidebarWidth={170}
          rightSidebarWidth={0}
          itemRenderer={({ item, itemContext, getItemProps, getResizeProps }) => {
            const status = item.itemProps['data-status'];
            return (
              <div {...getItemProps({
                style: {
                  ...item.itemProps.style,
                  opacity: status === 'Scaduta' ? 0.45 : 1,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px',
                  border: '1px solid rgba(255,255,255,0.4)'
                }
              })}>
                <Tooltip title={`Stato: ${status}`}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
                    {item.title}
                  </span>
                </Tooltip>
              </div>
            );
          }}
        />
      </Box>
      <Box display="flex" gap={2} mt={2} flexWrap="wrap">
        {Object.entries(statusColors).map(([k, v]) => (
          <Box key={k} display="flex" alignItems="center" gap={1}>
            <span style={{ width: 14, height: 14, background: v, display: 'inline-block', borderRadius: 3 }} />
            <Typography variant="caption">{k}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default PitchGantt;
